import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink } from "react-router";
import { focusRing } from "../../ui/index.js";
import { LocaleSwitch } from "../auth/index.js";
import { BackLink } from "./BackLink.js";

export interface ShellNavItem {
  to: string;
  icon: ReactNode;
  label: string;
  /** Подпись в боковой панели, если отличается от вкладки (на телефоне «Ещё», на широком экране «Настройки»). */
  wideLabel?: string;
  badge?: number;
  /** Красный счётчик (непрочитанные), а не янтарный (ждут ответа). */
  badgeTone?: "amber" | "red";
  /** Только в боковой панели широкого экрана (на телефоне пункт живёт в шапке или в «Ещё»). */
  wideOnly?: boolean;
}

/**
 * Оболочка кабинета, общая для владельца и админа. Одна вёрстка:
 * на телефоне шапка с салоном и колокольчиком плюс нижние вкладки, как раньше;
 * от `lg` (1024px) боковая панель слева (салон, навигация, язык), контент не шире ~1160px и по центру.
 * `bell`: колокольчик в шапке телефона (на широком экране он же пункт панели).
 */
export function CabinetShell({
  salonName,
  items,
  backTo,
  banner,
  bell,
  children,
}: {
  salonName: string;
  items: ShellNavItem[];
  backTo: string | null;
  banner?: ReactNode;
  bell?: { to: string; count: number };
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col lg:max-w-none lg:flex-row">
      {/* Колонка на всю высоту страницы, содержимое прилипает к верху при прокрутке. */}
      <aside className="hidden lg:block lg:w-64 lg:shrink-0 lg:border-r lg:border-slate-200 lg:bg-slate-50">
        <div className="sticky top-0 flex h-dvh flex-col">
          <div className="px-5 pt-6 pb-4">
            <div className="text-xs text-slate-500">{t("common.salon")}</div>
            <div className="truncate text-lg font-semibold">{salonName}</div>
          </div>
          <nav aria-label={t("nav.main")} className="flex flex-1 flex-col gap-1 px-3">
            {items.map((item) => (
              <SideLink key={item.to} item={item} />
            ))}
          </nav>
          <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-4">
            <span className="text-xs text-slate-500">{t("settings.language")}</span>
            <LocaleSwitch />
          </div>
        </div>
      </aside>

      {/* `--shell-banner`: высота плашки над содержимым (кабинет админа); страницы во весь экран вычитают её из высоты. */}
      <div className="flex min-h-dvh min-w-0 flex-1 flex-col" style={banner ? ({ "--shell-banner": "2.25rem" } as CSSProperties) : undefined}>
        <div className="sticky top-0 z-20 bg-white lg:static">
          {banner}
          <header className="flex items-center justify-between bg-white/95 px-4 py-3 backdrop-blur lg:hidden">
            <div className="min-w-0">
              {backTo && <BackLink to={backTo} className="mb-0.5 inline-block text-sm font-medium text-slate-800" />}
              <div className="text-xs text-slate-500">{t("common.salon")}</div>
              <div className="truncate font-semibold">{salonName}</div>
            </div>
            {bell && (
              <Link to={bell.to} className={`relative rounded-full p-2 text-xl ${focusRing}`} aria-label={t("nav.notifications")}>
                <span aria-hidden="true">🔔</span>
                {bell.count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-5 rounded-full bg-red-600 px-1 text-center text-xs font-semibold text-white">
                    {bell.count > 99 ? "99+" : bell.count}
                  </span>
                )}
              </Link>
            )}
          </header>
        </div>
        <main className="flex-1 px-4 pb-24 lg:mx-auto lg:w-full lg:max-w-[1160px] lg:px-8 lg:pt-6 lg:pb-10">
          {backTo && (
            <div className="hidden lg:block">
              <BackLink to={backTo} className="mb-3 inline-block text-sm font-medium text-slate-600 hover:text-slate-900" />
            </div>
          )}
          {children}
        </main>
        <nav
          aria-label={t("nav.main")}
          className="fixed inset-x-0 bottom-0 z-20 mx-auto flex max-w-md border-t border-slate-300 bg-slate-100 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_20px_rgba(15,23,42,0.14)] lg:hidden"
        >
          {items
            .filter((item) => !item.wideOnly)
            .map((item) => (
              <Tab key={item.to} item={item} />
            ))}
        </nav>
      </div>
    </div>
  );
}

const badgeClass = (tone: "amber" | "red" = "amber") =>
  `min-w-5 rounded-full px-1 text-center text-xs font-semibold text-white ${tone === "red" ? "bg-red-600" : "bg-amber-500"}`;

/** Вкладка нижней панели телефона. */
function Tab({ item }: { item: ShellNavItem }) {
  const badge = item.badge ?? 0;
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        `relative flex flex-1 flex-col items-center justify-center py-2 text-xs font-medium ${isActive ? "font-semibold text-slate-900" : "text-slate-700"}`
      }
    >
      <span className="text-xl leading-6" aria-hidden="true">
        {item.icon}
      </span>
      {item.label}
      {badge > 0 && <span className={`absolute top-1 left-1/2 ml-2 ${badgeClass(item.badgeTone)}`}>{badge}</span>}
    </NavLink>
  );
}

/** Пункт боковой панели широкого экрана. */
function SideLink({ item }: { item: ShellNavItem }) {
  const badge = item.badge ?? 0;
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${focusRing} ${
          isActive ? "bg-white text-slate-900 shadow-sm" : "text-slate-700 hover:bg-slate-200/70 hover:text-slate-900"
        }`
      }
    >
      <span className="w-6 text-center text-lg leading-6" aria-hidden="true">
        {item.icon}
      </span>
      <span className="flex-1 truncate">{item.wideLabel ?? item.label}</span>
      {badge > 0 && <span className={badgeClass(item.badgeTone)}>{badge > 99 ? "99+" : badge}</span>}
    </NavLink>
  );
}
